//
//  Run.tsx
//  TrajeXY Signet Server
//
//  Copyright © 2026 TrajeXY. All rights reserved.
//
//  Created: September 23, 2026
//  Author: Bob Keathley
//
//  Run detail page: timeline with a 5s poller for live rows, and a respawn
//  marker where a pane restarts within one run (spec 6e).
//
import { Fragment, useCallback, useEffect, useState } from 'react'
import { api } from '@shared/api/client'
import { isOk } from '@shared/api/envelope'
import { keyOf, useLiveTimeline } from '@shared/api/live'
import { templateRef, TIMELINE_TYPES, isCapture, isEvent, timelineType, isEvidence, type CaptureEntry, type EvidenceEntry, type EventEntry, type Run as RunT, type RunDetail, type RunTimelineEntry } from '@shared/api/types'
import { canTriageSignals, type RunSignal } from '@shared/api/signals'
import type { Session } from '@shared/auth'
import { href } from '@shared/router'
import { EstSpend, LiveDot } from './format'

const when = (s: number) => new Date(s * 1000).toLocaleString()
const bdg = (d: string) => <span className={`badge b-${d}`}>{d.toUpperCase()}</span>

export function RunHeader({ run }: { run: RunT }) {
  return (
    <>
      <h2>
        Run <span className="mono">{run.session_id.slice(0, 8)}…</span>{' '}
        <LiveDot
          live={run.live}
          lagS={Math.max(0, Math.round(Date.now() / 1000 - run.last_at))}
          lagTitle="approximate lag: local clock − last_at -- §6e's run-detail route carries no server_time; the Runs list readout uses the real one"
        />{' '}
        <span className="hint">{run.workstream_id ?? 'no workstream'}</span>
      </h2>
      <div className="grid">
        {run.agents.map((a) => (
          <div key={a.notebook_id} className="card">
            <div className="l">{a.agent}</div>
            {a.title && <div className="hint">{a.title}</div>}
            <div className="hint mono">template: {templateRef(a.template)}</div>
            {a.user && <div className="hint">user: {a.user.display_name} <span className="mono">{a.user.email}</span></div>}
            <div className="v">{a.deeds} deeds</div>
            <div className="hint">panes: {a.panes.length ? a.panes.join(' → ') : '—'}</div>
            <div className="hint">deny {a.deny} · watch {a.watch} · hold {a.hold}</div>
            <div className="hint"><EstSpend usd={a.usd} /></div>
          </div>
        ))}
      </div>
      {run.phases.length > 0 && <p className="hint">Phases: {run.phases.join(' → ')}</p>}
      <p className="hint">Total spend: <EstSpend usd={run.totals.usd_est} /></p>
    </>
  )
}

/** §6r evidence/request: non-authoritative (`authoritative: false`), shown for review only; summary/body are content-gated. */
function EvidenceRow({ e }: { e: EvidenceEntry }) {
  return (
    <tr>
      <td className="hint mono">{when(e.ts)}</td>
      <td><span className="pill" title="Non-authoritative: never read by the gate, a release or a ceiling check">evidence / suggestions · {e.type}</span></td>
      <td className="mono">{e.agent ?? "—"}</td>
      <td className="hint">non-authoritative</td>
      <td className="mono">{e.kind}</td>
      <td className="mono action">
        {e.content_redacted || !e.content
          ? <span className="hint">content hidden for your role</span>
          : <>{e.content.summary && <div>{e.content.summary}</div>}{e.content.body && <details><summary>body</summary><pre>{JSON.stringify(e.content.body, null, 2)}</pre></details>}</>}
      </td>
      <td className="hint">suggestion only: it never changes a decision</td>
      <td className="hint">—</td>
    </tr>
  )
}

/** Base64 of the original hook stdin; shown raw (still base64) when it is not valid UTF-8. */
export function decodePayload(b64: string): string {
  try {
    const bin = atob(b64)
    return new TextDecoder("utf-8", { fatal: true }).decode(Uint8Array.from(bin, (ch) => ch.charCodeAt(0)))
  } catch {
    return b64
  }
}

/** §6e/§6r capture row: metadata always; the raw payload sits behind an expander, content-gated by role. */
function CaptureRow({ c }: { c: CaptureEntry }) {
  return (
    <tr>
      <td className="hint mono">{when(c.ts)}</td>
      <td><span className="pill" title={c.host_event}>capture</span></td>
      <td className="mono">{c.agent ?? "—"}</td>
      <td className="hint">—</td>
      <td className="hint mono">{c.host}</td>
      <td className="mono action">
        {c.content_redacted || !c.content
          ? <span className="hint">content hidden for your role</span>
          : <details><summary>raw payload</summary><pre>{decodePayload(c.content.payload_b64)}</pre></details>}
      </td>
      <td className="hint mono">{c.size} B · sha256 {c.sha256.slice(0, 12)}…</td>
      <td className="hint">—</td>
    </tr>
  )
}

const show = (v: unknown) => (typeof v === 'string' ? v : JSON.stringify(v))

/** §6q event row. Metadata (`fields`) is shown to everyone; `content` only when the API returns it. */
function EventRow({ e }: { e: EventEntry }) {
  const f = e.fields
  const meta = Object.entries(f).filter(([k]) => k !== 'tool_name' && k !== 'to').map(([k, v]) => `${k} ${show(v)}`).join(' · ')
  return (
    <tr>
      <td className="hint mono">{when(e.ts)}</td>
      <td><span className="pill" title={e.host_event}>{e.event}</span></td>
      <td className="mono">
        {e.agent}
        {e.event === 'agent_message' && <span className="hint"> {e.agent} → {typeof f.to === 'string' ? f.to : '?'}</span>}
        {e.agent_id && <span className="hint"> ({e.agent_type ?? 'subagent'} {e.agent_id})</span>}
      </td>
      <td className="hint">—</td>
      <td className="mono">{typeof f.tool_name === 'string' ? f.tool_name : '—'}</td>
      <td className="mono action">
        {e.content_redacted || e.content === null
          ? <span className="hint">content hidden for your role</span>
          : Object.entries(e.content).map(([k, v]) => <div key={k}><span className="hint">{k}: </span>{show(v)}</div>)}
      </td>
      <td className="hint">{meta || '—'}</td>
      <td className="hint">—</td>
    </tr>
  )
}

/** Timeline rows, in the same badge/redaction convention as `Deeds.tsx`, plus a respawn marker
 * wherever `seen` changes within one `notebook_id` -- a pane restart within the same run (§6e). */
export function RunTimeline({ items }: { items: RunTimelineEntry[] }) {
  const redacted = items.some((d) => d.content_redacted)
  const lastSeenByNotebook = new Map<string, string | null>()
  return (
    <>
      {redacted && <p className="hint">🔒 Content hidden for your role; admins see command text, reasons and event content (06 §4a).</p>}
      <table>
        <thead><tr><th>When</th><th>Type</th><th>Agent</th><th>Decision</th><th>Tool</th><th>Content</th><th>Reason</th><th>Phase</th></tr></thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan={8} className="hint">No events yet: they appear once the Mac records them (Warp SIG-113).</td></tr>}
          {items.map((d) => {
            const key = keyOf(d)
            if (isEvent(d)) return <EventRow key={key} e={d} />
            if (isCapture(d)) return <CaptureRow key={key} c={d} />
            if (isEvidence(d)) return <EvidenceRow key={key} e={d} />
            const prevSeen = lastSeenByNotebook.get(d.notebook_id)
            const respawn = lastSeenByNotebook.has(d.notebook_id) && d.seen !== prevSeen
            lastSeenByNotebook.set(d.notebook_id, d.seen)
            return (
              <Fragment key={key}>
                {respawn && (
                  <tr className="respawn-marker">
                    <td colSpan={8} className="hint">↻ respawn — {d.agent} resumed in a new pane ({d.seen ?? '—'})</td>
                  </tr>
                )}
                <tr>
                  <td className="hint mono">{when(d.ts)}</td>
                  <td><span className="pill">{timelineType(d)}</span></td>
                  <td className="mono">{d.agent}</td>
                  <td>{bdg(d.decision)}{d.exception_id && <span className="exc-tag" title={`exception ${d.exception_id}`}>EXC</span>}</td>
                  <td className="mono">{d.tool}</td>
                  <td className="mono action">{d.content_redacted ? <span className="hint">🔒 redacted</span> : d.action}</td>
                  <td className="hint">{d.content_redacted ? "—" : d.reason}</td>
                  <td className="hint">{d.phase ?? '—'}</td>
                </tr>
              </Fragment>
            )
          })}
        </tbody>
      </table>
    </>
  )
}

/** §6u: one chip per signal on this run, linking to its detail; team-admin+ also get a link to flag a new one. */
export function SignalChips({ signals, role, sessionId, agent }: { signals: RunSignal[] | undefined; role?: string; sessionId: string; agent?: string }) {
  const list = signals ?? []
  const canFlag = canTriageSignals(role)
  if (list.length === 0 && !canFlag) return null
  return (
    <div className="signal-chips">
      {list.length > 0 && <span className="hint">Signals:</span>}
      {list.map((s) => <a key={s.id} className={`chip st-${s.status}`} href={href(`/signals/${s.id}`)} title={`${s.source} · ${s.status}`}><span className="mono">{s.id}</span> {s.summary}</a>)}
      {canFlag && <a className="hint" href={href(`/signals?session=${encodeURIComponent(sessionId)}${agent ? `&agent=${encodeURIComponent(agent)}` : ''}`)}>Flag a problem</a>}
    </div>
  )
}

const typesQ = (types: string[], sep = '?') => (types.length ? `${sep}types=${types.join(',')}` : '')

export default function Run({ session, sessionId }: { session: Session; sessionId: string }) {
  const [run, setRun] = useState<RunT | null>(null)
  const [runErr, setRunErr] = useState<string | null>(null)
  const [older, setOlder] = useState<RunTimelineEntry[]>([])
  const [next, setNext] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const onRun = useCallback((data: unknown) => {
    const d = data as RunDetail
    setRun(d)
    setRunErr(null)
  }, [])
  const [types, setTypes] = useState<string[]>([])
  const live = useLiveTimeline(sessionId, session.token, onRun, true, types)

  // First page: fetches the run header + the newest timeline page (no `after=`) so the page has
  // content before the 5s poller's first tick lands.
  useEffect(() => {
    setRun(null)
    setRunErr(null)
    setOlder([])
    setNext(null)
    api<RunDetail>(`/fleet/runs/${sessionId}${typesQ(types)}`, {}, session.token).then((r) => {
      if (isOk(r)) {
        setRun(r.data)
        setNext(r.data.timeline.next_cursor)
      } else {
        setRunErr(r.error.message)
      }
    })
  }, [sessionId, session.token, types])

  const loadOlder = useCallback(async (cursor: string) => {
    setBusy(true)
    const r = await api<RunDetail>(`/fleet/runs/${sessionId}?cursor=${encodeURIComponent(cursor)}${typesQ(types, '&')}`, {}, session.token)
    setBusy(false)
    if (!isOk(r)) { setErr(r.error.message); return }
    setOlder((prev) => [...prev, ...r.data.timeline.items])
    setNext(r.data.timeline.next_cursor)
  }, [sessionId, session.token, types])

  // Live rows merge in ahead of (newer than) the once-loaded "older" pages; de-duplication by
  // (notebook_id, seq) is already handled inside `useLiveTimeline`/`merge`.
  const seenKeys = new Set(live.items.map((d) => keyOf(d)))
  const items = [...live.items, ...older.filter((d) => !seenKeys.has(keyOf(d)))]

  return (
    <>
      <p><a href={href('/runs')}>‹ Runs</a></p>
      {runErr && <p className="err" role="alert">{runErr}</p>}
      {run === null && !runErr && <p className="hint">Loading run…</p>}
      {run?.source === "legacy" && <p className="hint" role="note">Legacy chain: every record in this run comes from the old Notebook chain, none from the activity stream.</p>}
      {run?.source === "mixed" && <p className="hint" role="note">Mixed sources: some records come from the activity stream, others from the legacy chain.</p>}
      {run && <RunHeader run={run} />}
      {run && <SignalChips signals={(run as RunT & { signals?: RunSignal[] }).signals} role={session.user.role} sessionId={sessionId} agent={run.agents[0]?.agent} />}
      <h2>Timeline</h2>
      <p>
        <button className={`btn sm ${types.length ? 'ghost' : ''}`} aria-pressed={types.length === 0} onClick={() => setTypes([])}>all</button>{' '}
        {TIMELINE_TYPES.map((t) => (
          <button key={t} className={`btn sm ${types.includes(t) ? '' : 'ghost'}`} aria-pressed={types.includes(t)} onClick={() => setTypes((p) => (p.includes(t) ? p.filter((x) => x !== t) : [...p, t]))}>{t}</button>
        ))}
      </p>
      {err && <p className="err" role="alert">{err}</p>}
      {live.error && <p className="err" role="alert">{live.error}</p>}
      <RunTimeline items={items} />
      {next !== null && <p><button className="btn ghost" disabled={busy} onClick={() => loadOlder(next)}>{busy ? 'Loading…' : 'Load older'}</button></p>}
    </>
  )
}
